import { Hospital, User, Calendar, Clock } from "lucide-react";
import { Button } from "../../components/ui/button";
import { TicketDto } from "@/src/web-api-client";
import { useLanguage } from "../../i18n/LanguageContext";

interface AppointmentCardProps {
  record: TicketDto;
  isMobile?: boolean;
  onViewDetail: (record: TicketDto) => void;
  getStatusBadge: (status: string) => React.ReactNode;
}

export function AppointmentCard({
  record,
  isMobile = false,
  onViewDetail,
  getStatusBadge,
}: AppointmentCardProps) {
  const { t } = useLanguage();
  return (
    <div className="border bg-white w-full rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex flex-col md:items-center justify-between">
        <div className="w-full">
          {/* Header: Mã phiếu + Tên bệnh nhân */}
          <div
            className="border-b-[1px] border-dashed border-gray-900 pb-3"
            style={{
              borderStyle: "solid",
              borderImage:
                "repeating-linear-gradient(to right, #000000ff 0, #000000ff 4px, transparent 4px, transparent 8px) 1",
            }}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span>{t("maphieu")}:</span>
                <span className="font-semibold text-medical-primary">
                  {record.appointmentId}
                </span>
              </div>
              <div>{getStatusBadge(record.status ?? t("khongro"))}</div>
            </div>

            <p className="font-medium text-base md:text-lg max-w-[80%]">
              {record.fullName}
            </p>
          </div>

          {/* Phòng khám */}
          <div className="flex justify-between md:justify-start items-start md:items-center mt-3">
            {!isMobile && <Hospital className="w-4 h-4 text-green-600 mr-4" />}
            <span className="text-gray-600 shrink-0 md:w-[120px]">
              {t("phongkhamlabel")}
            </span>
            <span className="max-w-[60%] text-end leading-tight">
              {record.clinicName}
            </span>
          </div>

          {/* Bác sĩ */}
          {record.doctorName && (
            <div className="flex justify-between md:justify-start items-center mt-3">
              {!isMobile && <User className="w-4 h-4 text-green-600 mr-4" />}
              <span className="text-gray-600 md:w-[120px]">{t("bacsilabel")}</span>
              <span>{record.doctorName}</span>
            </div>
          )}

          {/* Ngày khám */}
          <div className="flex justify-between md:justify-start items-center mt-1 md:mt-3">
            {!isMobile && <Calendar className="w-4 h-4 text-green-600 mr-4" />}
            <span className="text-gray-600 md:w-[120px]">{t("ngaykhamlabel")}</span>
            <span className="text-green-500 font-medium">
              {record.appointmentDate}
            </span>
          </div>

          {/* Giờ khám */}
          <div className="flex justify-between md:justify-start items-center mt-1 md:mt-3">
            {!isMobile && <Clock className="w-4 h-4 text-green-600 mr-4" />}
            <span className="text-gray-600 md:w-[120px]">{t("giokhamlabel")}</span>
            <span className="text-green-500 font-medium">
              {record.appointmentTime}
            </span>
          </div>
        </div>

        {/* Nút Xem chi tiết */}
        <Button
          variant="outline"
          size="sm"
          className="mt-4 md:ml-auto md:mt-6 border-medical-primary text-medical-primary hover:bg-medical-primary hover:text-white"
          onClick={() => onViewDetail(record)}
        >
          {t("xemchitiet")}
        </Button>
      </div>
    </div>
  );
}
